// help-manual-overlay-view.ts — the FULL-MANUAL in-game HELP DOM overlay (HTML/DOM UI
// shell plan, Phase 5). The in-game PAUSE MENU's HELP button (handleInGameMenuChoice
// kidcHelp) opened the canvas Help() manual — kidfHelpWide (240×160): a scrollable
// data/help.txt with an Index, Back, NextPage/PrevPage and an OK exit (forms.ini.pp
// kidfHelpWide). Unlike the build panels' "?" (which jumps to ONE context section — the
// section-only HelpView), the pause-menu HELP is the WHOLE manual from the top, exactly
// like the main-menu HELP. So this overlay reuses the SAME full-fidelity renderer the
// DOM main-menu HELP uses (mountHelpManual) — inline images, clickable cross-links,
// anchors, Index + (in-doc) Back — rendered as a modal overlay over the live (paused)
// canvas instead of a menu router screen.
//
// FAITHFUL to the canvas HelpForm (a ShellForm): a full-window modal over the title
// backdrop with a black letterbox (shellform.ts OnPaintBackground), the ShellForm zip-in
// cascade + form-show sfx (ShellDoModal fAnimate=true), on the SAME 240×160 design stage
// as the canvas kidfHelpWide so it renders at the identical scale. The C++ paginated
// NextPage/PrevPage become a single continuous scroll (the DOM idiom, matching the menu
// manual); INDEX + BACK are reproduced; OK exits (kidcOk). Esc also exits.
//
// DISPLAY-only: emits no command, constructs no Message. The SP pause is held by
// DoInGameMenu around the whole pause-menu interaction (handleInGameMenuChoice's finish
// un-pauses); this overlay's onClose just runs that finish — it does not touch the sim.

import type { ButtonArt } from '../title-art.ts';
import { ksfxGuiButtonTap } from '../../ui/uikit.ts';
import { zipInStage } from '../formchrome.ts';
import { mountHelpManual, type HelpManualHandle } from '../help-manual-view.ts';
import { fetchHelpText } from './help-view.ts';
import {
  COL_BG,
  COL_FG,
  TEXT_SHADOW,
  el,
  FONT_BODY,
  buttonWidthU,
  makeStage,
  at,
  formTitle,
  makeStageButton,
  type StageButtonSize,
  centeredBand,
} from '../chrome.ts';

export interface HelpManualOverlayDeps {
  /** the overlay container (created by the host; this view owns its contents). */
  root: HTMLElement;
  /** resolve the manual's inline <IMG SRC="*.anir"> to a PNG data URL (the pack's team-
   *  coloured 'help'-strip frame — the SAME resolveHelpImageDataUrl path the canvas
   *  HelpControl + the menu manual use). null ⇒ unresolved (alt text + warn). Absent ⇒
   *  images are dropped (e.g. no pack). */
  resolveImage?: (src: string) => string | null;
  /** the title-screen backdrop (titlescreenbkgd.png) as a CSS url; absent ⇒ flat. */
  titleBg?: string;
  /** the raw 9-slice button pieces — faithful chrome; absent ⇒ a CSS-button fallback. */
  buttonArt?: ButtonArt;
  /** play a GUI sfx (ksfxGui*) on a button/link press, wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
  /** OK / Esc → the host unmounts the overlay and runs the pause-menu finish epilogue
   *  (un-pause SP + done) — the SAME continuation the canvas Help()'s done ran. */
  onClose: () => void;
}

const FONT = FONT_BODY;

// the kidfHelpWide design layout, matched to the DOM main-menu HELP (menu-view.helpScreen):
// title "HELP" at (0,6,240); the manual region at (5,20,230,116); the nav button row at y141.
const TITLE_Y = 6;
const REGION = { x: 5, y: 20, w: 230, h: 116 };
const ROW_Y = 141,
  BTN_H = 12;

export class HelpManualOverlayView {
  private readonly d: HelpManualOverlayDeps;
  /** the shared 9-slice button renderer (owns its own composite cache; fontFloor 8). */
  private readonly btn: (
    label: string,
    on: () => void,
    size?: StageButtonSize,
  ) => HTMLButtonElement;
  /** the raw help.txt once fetched (null until /help.txt resolves). */
  private raw: string | null = null;
  private loadError = false;
  /** the mounted manual handle (Index/Back nav) — null until the manual is mounted. */
  private manual: HelpManualHandle | null = null;
  /** the current stage (for the nav-button refresh) — re-created each render(). */
  private stage: HTMLElement | null = null;
  /** the ShellForm zip plays ONCE, on the first show — not on re-renders. */
  private animated = false;
  private formShowTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;

  constructor(d: HelpManualOverlayDeps) {
    this.d = d;
    this.btn = makeStageButton(d); // fontFloor defaults to 8 — the in-game button
    const s = d.root.style;
    // a full-window MODAL overlay above #screen (z above the canvas at 10); pointer-events:all
    // so the paused canvas behind it gets nothing while it is up. Over the title backdrop +
    // black letterbox, exactly like the canvas Help ShellForm (and the GameOptions overlay).
    s.position = 'fixed';
    s.inset = '0';
    s.display = 'flex';
    s.alignItems = 'center';
    s.justifyContent = 'center';
    s.zIndex = '20';
    s.color = COL_FG;
    s.fontFamily = FONT;
    s.userSelect = 'none';
    s.pointerEvents = 'all';
    s.overflow = 'hidden'; // clip controls parked off-window during the zip (no scrollbar flash)
    s.backgroundImage = d.titleBg !== undefined ? `url(${d.titleBg})` : 'none';
    s.backgroundColor = COL_BG;
    s.backgroundSize = 'cover';
    s.backgroundPosition = 'center';
    s.backgroundRepeat = 'no-repeat';
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onKeyDown);
    this.render();
    // fetch the manual, then re-render with the real content.
    void fetchHelpText()
      .then((raw) => {
        if (this.closed) return;
        this.raw = raw;
        this.render();
      })
      .catch(() => {
        if (!this.closed) {
          this.loadError = true;
          this.render();
        }
      });
  }

  private readonly onResize = (): void => {
    this.render();
  };

  // Esc → exit (the canvas ShellForm dismiss → kidcOk).
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.close();
    }
  };

  /** detach listeners + cancel a pending form-show sfx (the host unmounts on close). */
  destroy(): void {
    this.closed = true;
    this.manual = null;
    this.stage = null;
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKeyDown);
    if (this.formShowTimer !== null) {
      clearTimeout(this.formShowTimer);
      this.formShowTimer = null;
    }
  }

  private close(): void {
    if (this.closed) return;
    this.closed = true; // guard re-entrancy before the host unmounts (which also sets it)
    this.d.playSfx?.(ksfxGuiButtonTap);
    this.d.onClose();
  }

  // ── the faithful design stage (kidfHelpWide is 240×160) ───────────────────────────
  render(): void {
    if (this.closed) return;
    const { stage, s } = makeStage(240, 160);
    // makeStage returns a position:relative stage; in the flex-centred root an OVERSIZED stage
    // (the 240u form at the phone-portrait boost is wider than the viewport) gets pinned to the
    // flex START instead of centred, shifting it off the right edge. Centre it with absolute +
    // translate (like the shell views' makeStage) so it stays viewport-centred even when it
    // overflows the width — the body is already reflowed into the centred band (centeredBand).
    Object.assign(stage.style, {
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
    });
    this.stage = stage;

    // title "HELP" (centred, full-width like the other shell screens).
    stage.append(at(formTitle('HELP', s), s, 0, TITLE_Y, 240));

    // the manual body region (the HELP region, below the title). A host div the manual
    // mounts into; [data-list] excludes it from the form zip.
    const host = el('div', { position: 'absolute', inset: '0', overflow: 'hidden' });
    host.dataset.list = '1';
    // portrait boost: reflow the manual body into the centered band (no-op otherwise).
    const rb = centeredBand(REGION.x, REGION.w);
    const region = at(host, s, rb.x, REGION.y, rb.w, REGION.h);

    if (this.raw !== null && this.raw.length > 0) {
      // the rich manual (RAW help.txt → images / cross-links / Index / Back). onNavChange
      // re-renders just the nav row so the in-doc BACK enables/disables as history grows.
      this.manual = mountHelpManual(host, s, {
        raw: this.raw,
        resolveImage: (src) => this.d.resolveImage?.(src) ?? null,
        playSfx: this.d.playSfx,
        sfxTap: ksfxGuiButtonTap,
        onNavChange: () => this.refreshNav(s),
      });
    } else {
      this.manual = null;
      const msg = el('div', {
        overflowY: 'auto',
        whiteSpace: 'pre-wrap',
        lineHeight: '1.4',
        fontFamily: FONT,
        fontSize: `${Math.round(5.5 * s)}px`,
        color: COL_FG,
        textShadow: TEXT_SHADOW,
        background: 'rgba(7,22,27,0.55)',
        border: `${Math.max(1, Math.round(s))}px solid rgba(143,208,224,0.35)`,
        boxSizing: 'border-box',
        padding: `${2 * s}px ${4 * s}px`,
        width: '100%',
        height: '100%',
      });
      msg.textContent = this.loadError ? 'Help is unavailable.' : 'Loading…';
      host.append(msg);
    }
    stage.append(region);

    // the bottom nav row. With the rich manual: INDEX (→ top) + BACK (pop the in-doc
    // history) + OK (exit — the canvas kidcOk). Without it (loading / error): just OK.
    this.refreshNav(s);

    this.d.root.replaceChildren(stage);
    // the ShellForm zip + form-show sfx play on the FIRST show only (ShellDoModal
    // fAnimate=true); resize / re-render rebuild the stage WITHOUT re-zipping.
    if (!this.animated) {
      this.animated = true;
      this.zipIn(stage);
    }
  }

  // (re)render just the nav button row so the in-doc BACK reflects canGoBack() after an
  // Index/Back/link jump (the manual body itself is untouched — its scroll is view state).
  // Mirrors menu-view.refreshHelpButtons, with the in-game button + OK (kidcOk) exit.
  private refreshNav(s: number): void {
    const stage = this.stage;
    if (stage === null) return;
    stage.querySelectorAll('[data-help-nav]').forEach((n) => n.remove());
    const m = this.manual;
    const row: { label: string; on: () => void; en: boolean }[] =
      m === null
        ? [{ label: 'OK', on: () => this.close(), en: true }]
        : [
            { label: 'INDEX', on: () => m.toIndex(), en: true },
            { label: 'BACK', on: () => m.goBack(), en: m.canGoBack() },
            { label: 'OK', on: () => this.close(), en: true },
          ];
    const sized = row.map((b) => ({ ...b, w: buttonWidthU(b.label, BTN_H, s) }));
    const gapU = 5;
    const totalW = sized.reduce((a, b) => a + b.w, 0) + gapU * (sized.length - 1);
    let x = Math.max(0, (240 - totalW) / 2);
    for (const b of sized) {
      // a disabled BACK still renders (greyed via opacity); the click no-ops.
      const button = this.btn(b.en ? b.label : b.label, b.on, { w: b.w * s, h: BTN_H * s });
      if (!b.en) {
        button.style.opacity = '0.5';
        button.disabled = true;
      }
      const placed = at(button, s, x, ROW_Y);
      placed.dataset.helpNav = '1';
      stage.append(placed);
      x += b.w + gapU;
    }
  }

  // the ShellForm zip-in — the SAME faithful cascade the sibling in-game views use.
  private zipIn(stage: HTMLElement): void {
    if (this.formShowTimer !== null) clearTimeout(this.formShowTimer);
    this.formShowTimer = zipInStage(stage, this.d.playSfx);
  }
}
